import { Button, Divider, Flex, Input, Select, Text } from '@chakra-ui/react';
import { DesignPreview } from 'modules/DesignGenerator/components/DesignPreview';
import { newStore } from 'modules/Editor/store';
import { ChatData, MessageType } from 'modules/common/components/Chat/Chat.types';
import { ChangeEvent, Dispatch, SetStateAction, useState } from 'react';
import { Provider } from 'react-redux';

import {
  BASE_CHART_TEMPLATE_ID_LIST,
  BASE_INFOGRAPH_TEMPLATE_ID_LIST,
  BASE_POSTER_TEMPLATE_ID_LIST,
  categoryGenerationTypeMap,
} from '../DesignAI.config';
import { generateDesignByAPI } from '../DesignAI.helpers';
import { DesignAICategories } from '../DesignAIContext';

type TestDesignGenerationProps = {
  chatData: ChatData[];
  setChatData: Dispatch<SetStateAction<ChatData[]>>;
};

const designAICategories = Object.values(DesignAICategories);

export const TestDesignGeneration = ({ chatData, setChatData }: TestDesignGenerationProps) => {
  const [designID, setDesignID] = useState<string>('');
  const [category, setCategory] = useState<DesignAICategories>(DesignAICategories.Infographics);
  const [userContext, setUserContext] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const handleDesignIDInputChange = (event: ChangeEvent<HTMLInputElement>) => setDesignID(event.target.value);
  const handleUserContextChange = (event: ChangeEvent<HTMLInputElement>) => setUserContext(event.target.value);
  const handleDesignIDSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setDesignID(event.target.value);
    setCategory(event.target.name as DesignAICategories);
  };
  const handleCategoryChange = (event: ChangeEvent<HTMLSelectElement>) =>
    setCategory(event.target.value as DesignAICategories);
  const handleGenerateClick = async () => {
    setIsGenerating(true);

    try {
      // Add the generating message
      setChatData((prev) => [
        ...prev,
        {
          type: MessageType.BOT_GENERATING,
          messages: ['start', 'It is generating a new design in debug mode'],
          createdAt: Date.now(),
        },
      ]);

      // Generating user design
      const generationType = categoryGenerationTypeMap[category];
      const newInfographState = await generateDesignByAPI({
        e2DesignId: designID,
        userContext: userContext,
        generationType,
      });

      setChatData((prev) => {
        return [
          ...prev.map((data) => {
            if (data.type === MessageType.BOT_GENERATING) data.messages[0] = 'stop';
            return data;
          }),
          {
            type: MessageType.DESIGN,
            messages: [designID, userContext],
            children: (
              <Provider store={newStore()}>
                <DesignPreview fitWidth={true} infographState={newInfographState} />
              </Provider>
            ),
            createdAt: Date.now(),
          },
        ];
      });
    } catch (error) {
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Flex w='full' gap='1' flexDirection='column'>
      <h3>Infographic</h3>
      <Select
        placeholder='Select infographic'
        name={DesignAICategories.Infographics}
        onChange={handleDesignIDSelectChange}
      >
        {BASE_INFOGRAPH_TEMPLATE_ID_LIST.map((data, index) => (
          <option key={index} value={data.e2DesignId}>
            {data.e2DesignTitle}
          </option>
        ))}
      </Select>

      <h3>Poster</h3>
      <Select placeholder='Select poster' name={DesignAICategories.Posters} onChange={handleDesignIDSelectChange}>
        {BASE_POSTER_TEMPLATE_ID_LIST.map((data, index) => (
          <option key={index} value={data.e2DesignId}>
            {data.e2DesignTitle}
          </option>
        ))}
      </Select>

      <h3>Charts</h3>
      <Select placeholder='Select charts' name={DesignAICategories.Charts} onChange={handleDesignIDSelectChange}>
        {BASE_CHART_TEMPLATE_ID_LIST.map((data, index) => (
          <option key={index} value={data.e2DesignId}>
            {data.e2DesignTitle}
          </option>
        ))}
      </Select>

      <Divider my='1' />

      <Text mt='2'>Design Category: </Text>
      <Select onChange={handleCategoryChange} value={category}>
        {designAICategories.map((category) => (
          <option value={category}>{category.slice(0, 1).toUpperCase() + category.slice(1)}</option>
        ))}
      </Select>

      <Text mt='2'>Design ID:</Text>
      <Input placeholder='E2 design ID' name='' onChange={handleDesignIDInputChange} value={designID} />
      <Text mt='2'>User Context:</Text>
      <Input placeholder='Context' name='' onChange={handleUserContextChange} value={userContext} />
      <Button isDisabled={isGenerating} variant={'action'} onClick={handleGenerateClick}>
        Generate
      </Button>
    </Flex>
  );
};
